import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UserPlus, Edit2, Trash2, Loader2, Shield, User, Users } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { t, language } = useLanguage();
  const { isSuperadmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'monteur'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/users`, form);
      toast.success(language === 'nl' ? 'Gebruiker aangemaakt' : language === 'de' ? 'Benutzer erstellt' : 'User created');
      setCreateOpen(false);
      setForm({ email: '', password: '', name: '', role: 'monteur' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'nl' ? 'Aanmaken mislukt' : language === 'de' ? 'Erstellung fehlgeschlagen' : 'Creation failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        name: selectedUser.name,
        role: selectedUser.role,
        is_active: selectedUser.is_active
      });
      toast.success(language === 'nl' ? 'Gebruiker bijgewerkt' : language === 'de' ? 'Benutzer aktualisiert' : 'User updated');
      setEditOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'nl' ? 'Bijwerken mislukt' : language === 'de' ? 'Aktualisierung fehlgeschlagen' : 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(language === 'nl' ? 'Weet je zeker dat je deze gebruiker wilt verwijderen?' : language === 'de' ? 'Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?' : 'Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success(language === 'nl' ? 'Gebruiker verwijderd' : language === 'de' ? 'Benutzer gelöscht' : 'User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'nl' ? 'Verwijderen mislukt' : language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed'));
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      superadmin: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/20 dark:text-purple-400 dark:border-purple-500/30',
      admin: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30',
      monteur: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30'
    };
    return styles[role] || 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-zinc-500/20 dark:text-zinc-400 dark:border-zinc-500/30';
  };

  const getRoleIcon = (role) => {
    if (role === 'superadmin') return Shield;
    if (role === 'admin') return Users;
    return User;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">{t('nav.users')}</h1>
        
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 btn-glow" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              {t('users.add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">{t('users.add')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">{t('users.name')} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-background border-border text-foreground"
                  required
                  data-testid="create-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">{t('auth.email')} *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-background border-border text-foreground"
                  required
                  data-testid="create-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">{t('auth.password')} *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-background border-border text-foreground"
                  required
                  data-testid="create-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">{t('users.role')} *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-background border-border text-foreground" data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="monteur" className="text-foreground hover:bg-accent">{t('role.monteur')}</SelectItem>
                    <SelectItem value="admin" className="text-foreground hover:bg-accent">{t('role.admin')}</SelectItem>
                    {isSuperadmin && (
                      <SelectItem value="superadmin" className="text-foreground hover:bg-accent">{t('role.superadmin')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="border-border text-foreground">
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving} data-testid="create-user-submit">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {t('common.add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users list */}
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-foreground">{language === 'nl' ? 'Alle gebruikers' : language === 'de' ? 'Alle Benutzer' : 'All users'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {language === 'nl' ? 'Geen gebruikers gevonden' : language === 'de' ? 'Keine Benutzer gefunden' : 'No users found'}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {users.map((user) => {
                const RoleIcon = getRoleIcon(user.role);
                const canEdit = isSuperadmin || (user.role !== 'superadmin');
                const canDelete = user.role !== 'superadmin' && user.id !== currentUser?.id;
                
                return (
                  <div key={user.id} className="flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <div className="p-2 rounded-lg bg-muted">
                      <RoleIcon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-foreground font-medium truncate">{user.name}</p>
                        {!user.is_active && (
                          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 text-xs">
                            {language === 'nl' ? 'Inactief' : language === 'de' ? 'Inaktiv' : 'Inactive'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm truncate">{user.email}</p>
                    </div>
                    <Badge variant="outline" className={getRoleBadge(user.role)}>
                      {t(`role.${user.role}`)}
                    </Badge>
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setSelectedUser({ ...user });
                            setEditOpen(true);
                          }}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{t('common.edit')} {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">{t('users.name')}</Label>
                <Input
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  className="bg-background border-border text-foreground"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">{t('users.role')}</Label>
                <Select 
                  value={selectedUser.role} 
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}
                  disabled={selectedUser.role === 'superadmin' && !isSuperadmin}
                >
                  <SelectTrigger className="bg-background border-border text-foreground" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="monteur" className="text-foreground hover:bg-accent">{t('role.monteur')}</SelectItem>
                    <SelectItem value="admin" className="text-foreground hover:bg-accent">{t('role.admin')}</SelectItem>
                    {isSuperadmin && (
                      <SelectItem value="superadmin" className="text-foreground hover:bg-accent">{t('role.superadmin')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-foreground">{t('users.active')}</Label>
                <Switch
                  checked={selectedUser.is_active}
                  onCheckedChange={(checked) => setSelectedUser({ ...selectedUser, is_active: checked })}
                  data-testid="edit-user-active"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-border text-foreground">
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving} data-testid="edit-user-submit">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {t('common.save')}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
